import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiGlobe, FiMapPin, FiMail } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Contact = () => {
  const [formStatus, setFormStatus] = useState('idle');

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulate API call
    setTimeout(() => {
      setFormStatus('success');
      e.target.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
    }, 1000);
  };

  return (
    <section id="contact" className="section-padding bg-navy relative overflow-hidden">
      {/* Decorative BG */}
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 bg-[url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
      
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left Column - Text & Info */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-[2px] bg-gold"></div>
              <span className="text-gold font-semibold uppercase tracking-wider text-sm">Make an Appointment</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Get Your Appraisal Quote</h2>
            <p className="text-gray-300 text-lg mb-10 leading-relaxed max-w-lg">
              Whether you're a homeowner, buyer, or investor, getting an expert property appraisal has never been easier. Fill out the form or reach out directly.
            </p>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-2xl space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold flex-shrink-0">
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Call Us Anywhere in Texas</p>
                  <a href="tel:+12148854504" className="text-white font-serif font-bold text-xl hover:text-gold transition-colors">+1-214-885-4504</a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold flex-shrink-0">
                  <SafeIcon icon={FiGlobe} className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Website</p>
                  <a href="https://epovaluation.com" className="text-white font-medium hover:text-gold transition-colors">epovaluation.com</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold flex-shrink-0">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Location</p>
                  <p className="text-white font-medium">Dallas, Texas</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white p-8 md:p-10 rounded-2xl shadow-2xl"
          >
            <h3 className="text-2xl font-serif font-bold text-navy mb-6">Request an Appraisal</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Name *</label>
                  <input type="text" required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all" placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Phone *</label>
                  <input type="tel" required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all" placeholder="(214) 885-4504" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Email *</label>
                <input type="email" required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all" placeholder="john@example.com" />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Preferred Date *</label>
                <input type="date" required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all text-gray-600" />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Property Details / Message *</label>
                <textarea required rows="4" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all resize-none" placeholder="Tell us about the property..."></textarea>
              </div>

              <button 
                type="submit" 
                disabled={formStatus === 'submitting'}
                className="w-full btn-primary py-4 text-lg mt-2"
              >
                {formStatus === 'submitting' ? 'Sending...' : formStatus === 'success' ? 'Message Sent!' : 'Submit Now'}
              </button>
            </form>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Contact;